import React, { useState, useEffect } from "react";
import { StyleSheet, View, ScrollView, ActivityIndicator, Clipboard } from "react-native";
import { useFirebase } from "@/components/firebase/useFirebase";
import { onAuthStateChanged, User } from "firebase/auth";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedButton } from "@/components/ThemedButton";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import { router } from "expo-router";

export default function TestAuthScreen() {
  const services = useFirebase();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [token, setToken] = useState<string>("");
  const [claims, setClaims] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  const cardBg = useThemeColor({}, "card");
  const borderCol = useThemeColor({}, "border");
  const tintCol = useThemeColor({}, "tint");

  useEffect(() => {
    if (!services?.auth) return;

    const unsubscribe = onAuthStateChanged(services.auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult(true);
          setToken(tokenResult.token);
          setClaims(tokenResult.claims);
        } catch (error) {
          console.error("Error fetching token/claims:", error);
        }
      } else {
        setToken("");
        setClaims({});
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [services]);

  const handleCopyToken = () => {
    if (!token) return;
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(token);
    } else {
      Clipboard.setString(token);
    }
    CustomAlert.alert("Copied", "Auth JWT Token copied to clipboard!");
  };

  const handleRefresh = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const tokenResult = await currentUser.getIdTokenResult(true);
      setToken(tokenResult.token);
      setClaims(tokenResult.claims);
      CustomAlert.alert("Refreshed", "Claims and token successfully refreshed.");
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "Failed to refresh token.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemedScreen
      title="Auth Diagnostics"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/test" as any),
      }}
    >
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        {loading ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color={tintCol} />
            <ThemedText style={{ marginTop: 12 }}>Loading auth state...</ThemedText>
          </View>
        ) : !currentUser ? (
          <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor: borderCol }]}>
            <ThemedText type="defaultSemiBold" style={{ textAlign: "center", marginBottom: 12 }}>
              Not Authenticated
            </ThemedText>
            <ThemedText style={{ textAlign: "center", opacity: 0.6 }}>
              There is no active authenticated Firebase session detected.
            </ThemedText>
          </ThemedView>
        ) : (
          <View style={styles.content}>
            {/* User Profile Card */}
            <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor: borderCol }]}>
              <ThemedText type="subtitle" style={styles.sectionTitle}>
                User Profile
              </ThemedText>

              <View style={styles.row}>
                <ThemedText style={styles.label}>Email:</ThemedText>
                <ThemedText style={styles.value}>{currentUser.email || "N/A"}</ThemedText>
              </View>

              <View style={styles.row}>
                <ThemedText style={styles.label}>UID:</ThemedText>
                <ThemedText style={styles.value}>{currentUser.uid}</ThemedText>
              </View>

              <View style={styles.row}>
                <ThemedText style={styles.label}>Name:</ThemedText>
                <ThemedText style={styles.value}>{currentUser.displayName || "N/A"}</ThemedText>
              </View>

              <View style={styles.row}>
                <ThemedText style={styles.label}>Email Verified:</ThemedText>
                <ThemedText style={[styles.value, currentUser.emailVerified ? { color: "#22C55E" } : { color: "#EF4444" }]}>
                  {currentUser.emailVerified ? "Yes" : "No"}
                </ThemedText>
              </View>

              <View style={styles.row}>
                <ThemedText style={styles.label}>Providers:</ThemedText>
                <ThemedText style={styles.value}>
                  {currentUser.providerData.map((p) => p.providerId).join(", ") || "None"}
                </ThemedText>
              </View>
            </ThemedView>

            {/* Custom Claims Card */}
            <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor: borderCol }]}>
              <ThemedText type="subtitle" style={styles.sectionTitle}>
                Custom Claims
              </ThemedText>
              <ScrollView style={styles.codeContainer} nestedScrollEnabled={true}>
                <ScrollView horizontal={true} showsHorizontalScrollIndicator={true}>
                  <ThemedText style={styles.codeText}>
                    {JSON.stringify(claims, null, 2)}
                  </ThemedText>
                </ScrollView>
              </ScrollView>
            </ThemedView>

            {/* JWT ID Token Card */}
            <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor: borderCol }]}>
              <ThemedText type="subtitle" style={styles.sectionTitle}>
                Auth ID Token (JWT)
              </ThemedText>
              <ThemedText style={styles.tokenText} numberOfLines={4} ellipsizeMode="tail">
                {token}
              </ThemedText>
              <View style={styles.btnRow}>
                <ThemedButton
                  title="Copy Token"
                  onPress={handleCopyToken}
                  style={{ flex: 1, marginRight: 8 }}
                />
                <ThemedButton
                  title="Refresh Claims"
                  variant="secondary"
                  onPress={handleRefresh}
                  style={{ flex: 1 }}
                />
              </View>
            </ThemedView>
          </View>
        )}
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  loaderContainer: {
    paddingVertical: 64,
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    gap: 16,
  },
  card: {
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 12,
  },
  row: {
    flexDirection: "row",
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(150, 150, 150, 0.1)",
  },
  label: {
    width: 90,
    fontWeight: "600",
    opacity: 0.6,
  },
  value: {
    flex: 1,
  },
  codeContainer: {
    backgroundColor: "rgba(150, 150, 150, 0.08)",
    borderRadius: 8,
    padding: 12,
    maxHeight: 200,
  },
  codeText: {
    fontFamily: "monospace",
    fontSize: 12,
  },
  tokenText: {
    fontFamily: "monospace",
    fontSize: 11,
    backgroundColor: "rgba(150, 150, 150, 0.08)",
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    lineHeight: 16,
  },
  btnRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
});
